import React, { useState, useEffect, useCallback } from "react";
import { getStatuses, createStatus, updateStatus, deleteStatus, changePassword, reorderStatuses } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  GripVertical,
  Flag,
  CheckCircle,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

export const SettingsPage = () => {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [reordering, setReordering] = useState(false);

  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    color: "#3B82F6",
    orderIndex: 0,
    isDefault: false,
    isFinal: false,
    isConversion: false,
  });

  const fetchStatuses = async () => {
    try {
      const response = await getStatuses();
      setStatuses(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des statuts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  const handleOpenForm = (status = null) => {
    if (status) {
      setEditingStatus(status);
      setFormData({
        name: status.name,
        color: status.color,
        orderIndex: status.orderIndex,
        isDefault: status.isDefault,
        isFinal: status.isFinal,
        isConversion: status.isConversion || false,
      });
    } else {
      setEditingStatus(null);
      setFormData({
        name: "",
        color: "#3B82F6",
        orderIndex: statuses.length,
        isDefault: false,
        isFinal: false,
        isConversion: false,
      });
    }
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error("Le nom est requis");
      return;
    }

    try {
      if (editingStatus) {
        await updateStatus(editingStatus.id, formData);
        toast.success("Statut mis à jour");
      } else {
        await createStatus(formData);
        toast.success("Statut créé");
      }
      setFormOpen(false);
      fetchStatuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async () => {
    if (!statusToDelete) return;
    try {
      await deleteStatus(statusToDelete);
      toast.success("Statut supprimé");
      setDeleteDialogOpen(false);
      setStatusToDelete(null);
      fetchStatuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }
    
    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success("Mot de passe modifié avec succès");
      setPasswordDialogOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setChangingPassword(false);
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.outerHTML);
    e.target.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder the array
    const newStatuses = [...statuses];
    const [draggedItem] = newStatuses.splice(draggedIndex, 1);
    newStatuses.splice(dropIndex, 0, draggedItem);
    
    // Update local state immediately for responsive UI
    setStatuses(newStatuses);
    setDraggedIndex(null);
    setDragOverIndex(null);

    // Save to backend
    await saveNewOrder(newStatuses);
  };

  const saveNewOrder = async (newStatuses) => {
    setReordering(true);
    try {
      const statusIds = newStatuses.map(s => s.id);
      await reorderStatuses(statusIds);
      toast.success("Ordre des statuts mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la réorganisation");
      // Revert on error
      fetchStatuses();
    } finally {
      setReordering(false);
    }
  };

  // Move up/down with buttons
  const moveStatus = async (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= statuses.length) return;

    const newStatuses = [...statuses];
    const temp = newStatuses[index];
    newStatuses[index] = newStatuses[newIndex];
    newStatuses[newIndex] = temp;

    setStatuses(newStatuses);
    await saveNewOrder(newStatuses);
  };

  const colorPresets = [
    "#3B82F6", // Blue
    "#8B5CF6", // Purple
    "#10B981", // Green
    "#F59E0B", // Yellow
    "#EF4444", // Red
    "#EC4899", // Pink
    "#06B6D4", // Cyan
    "#6B7280", // Gray
  ];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="settings-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Paramètres</h1>
          <p className="text-muted-foreground mt-1">
            Configurez les statuts et les paramètres du CRM
          </p>
        </div>
      </div>

      {/* Account Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Mon compte
          </CardTitle>
          <CardDescription>
            Gérez la sécurité de votre compte
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div>
              <p className="font-medium">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setPasswordDialogOpen(true)}
              data-testid="change-password-button"
            >
              <Lock className="h-4 w-4 mr-2" />
              Modifier le mot de passe
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Status Configuration */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Statuts des leads</CardTitle>
            <CardDescription>
              Définissez les étapes de votre pipeline commercial. Glissez-déposez pour réorganiser.
            </CardDescription>
          </div>
          <Button onClick={() => handleOpenForm()} data-testid="add-status-button">
            <Plus className="h-4 w-4 mr-2" />
            Nouveau statut
          </Button>
        </CardHeader>
        <CardContent>
          {statuses.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Flag className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucun statut configuré</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => handleOpenForm()}
              >
                Créer le premier statut
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {reordering && (
                <div className="flex items-center justify-center py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enregistrement...
                </div>
              )}
              {statuses.map((status, index) => (
                <div
                  key={status.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`flex items-center gap-4 p-4 rounded-lg border bg-card transition-all duration-200 ${
                    dragOverIndex === index && draggedIndex !== index
                      ? "border-primary border-2 bg-primary/5"
                      : "hover:bg-muted/50"
                  } ${draggedIndex === index ? "opacity-50" : ""}`}
                  data-testid={`status-item-${status.id}`}
                >
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => moveStatus(index, "up")}
                      disabled={index === 0 || reordering}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab active:cursor-grabbing" />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => moveStatus(index, "down")}
                      disabled={index === statuses.length - 1 || reordering}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </div>
                  <div
                    className="w-5 h-5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: status.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{status.name}</span>
                      {status.isDefault && (
                        <Badge variant="secondary" className="text-xs">
                          Par défaut
                        </Badge>
                      )}
                      {status.isFinal && (
                        <Badge variant="outline" className="text-xs">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Final
                        </Badge>
                      )}
                      {status.isConversion && (
                        <Badge className="text-xs bg-green-500 hover:bg-green-600">
                          Conversion
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Position: {index + 1}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleOpenForm(status)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Modifier
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => {
                          setStatusToDelete(status.id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingStatus ? "Modifier le statut" : "Nouveau statut"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom du statut</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Nouveau, En cours, Converti..."
                data-testid="status-form-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Couleur</Label>
              <div className="flex flex-wrap gap-2">
                {colorPresets.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, color }))}
                    className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                      formData.color === color ? "ring-2 ring-offset-2 ring-primary" : ""
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                  placeholder="#3B82F6"
                  className="flex-1 font-mono"
                  data-testid="status-form-color"
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="isDefault">Statut par défaut</Label>
                <p className="text-xs text-muted-foreground">
                  Attribué automatiquement aux nouveaux leads
                </p>
              </div>
              <Switch
                id="isDefault"
                checked={formData.isDefault}
                onCheckedChange={(v) => setFormData((prev) => ({ ...prev, isDefault: v }))}
                data-testid="status-form-default"
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="isFinal">Statut final</Label>
                <p className="text-xs text-muted-foreground">
                  Indique une fin de parcours (converti, perdu...)
                </p>
              </div>
              <Switch
                id="isFinal"
                checked={formData.isFinal}
                onCheckedChange={(v) => setFormData((prev) => ({ ...prev, isFinal: v }))}
                data-testid="status-form-final"
              />
            </div>

            <div className="flex items-center justify-between py-2 bg-green-50 dark:bg-green-950/30 rounded-lg px-3 -mx-3">
              <div>
                <Label htmlFor="isConversion" className="text-green-700 dark:text-green-400">
                  Statut de conversion
                </Label>
                <p className="text-xs text-muted-foreground">
                  Comptabilisé dans le taux de conversion (ex: Client, Vendu)
                </p>
              </div>
              <Switch
                id="isConversion"
                checked={formData.isConversion}
                onCheckedChange={(v) => setFormData((prev) => ({ ...prev, isConversion: v }))}
                data-testid="status-form-conversion"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="status-form-submit">
                {editingStatus ? "Enregistrer" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce statut ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Assurez-vous qu'aucun lead n'utilise ce statut.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Modifier le mot de passe
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Mot de passe actuel</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Minimum 6 caractères</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmer le nouveau mot de passe</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-destructive">Les mots de passe ne correspondent pas</p>
              )}
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setPasswordDialogOpen(false);
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={changingPassword || newPassword !== confirmPassword}
                data-testid="submit-password-change"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Modification...
                  </>
                ) : (
                  "Modifier"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
